import { clientPath, lpRequest } from '../../../../core/utils/lp-backend';

/** `GET /client/:cid`: the client with its settings merged with loop-pro's defaults. */
export const readClient = (): Promise<any> => lpRequest('GET', clientPath());

/**
 * `GET /client/:cid/config/fields`: the root fields `GET /client/:cid` leaves out (`payment_types`,
 * `task_pause_reasons`, `qr_typing_rights`, `secondary_driver_rights`, `allow_custom_payment_fields`, `partners`),
 * `null` where never set.
 */
export const readConfigFields = (): Promise<any> => lpRequest('GET', clientPath('/config/fields'));

/**
 * The client in cb's `/settings` shape: the `settings` map flattened, plus the root lists and flags, the ones
 * `GET /client/:cid` leaves out taken from `config` (`GET /client/:cid/config/fields`).
 */
export const toCbSettings = (client: any, config: any = {}): any => {
  const settings = client.settings || {};

  return {
    ...settings,
    id: client.id,
    cluster: { ...settings.cluster, lock: settings.cluster?.lock || {} },
    pooling: settings.pooling || {},
    hub: settings.hub || { cross_clustering: { enabled: false } },
    terminate_orders: settings.terminate_orders || {},
    ratings: client.ratings || [],
    abandonment_reasons: client.abandonment_reasons || [],
    partial_delivery_reasons:
      settings.partial_delivery_reasons || client.partial_delivery_reasons || [],
    task_pause_reasons: config.task_pause_reasons || [],
    sms_enabled: client.sms_enabled,
    vehicle_types: client.vehicle_types || settings.vehicle_types || [],
    payment_types: config.payment_types || [],
    qr_typing_rights: config.qr_typing_rights || false,
    secondary_driver_rights: config.secondary_driver_rights || false,
    use_third_party_api: client.use_third_party_api,
    partners: config.partners || []
  };
};

export const readSettings = async (): Promise<any> => {
  const [client, config] = await Promise.all([readClient(), readConfigFields()]);

  return toCbSettings(client, config);
};

/** `PATCH /client/settings/fields`: writes only the `settings` keys sent, then the settings as cb returned them. */
export const writeSettingsFields = async (fields: Record<string, any>): Promise<any> => {
  await lpRequest('PATCH', '/client/settings/fields', { body: fields });

  return readSettings();
};

/** `PATCH /client/config/fields`: the root `ratings`, `abandonment_reasons`, `task_pause_reasons`, ... */
export const writeConfigFields = async (fields: Record<string, any>): Promise<any> => {
  await lpRequest('PATCH', '/client/config/fields', { body: fields });

  return readSettings();
};
